import { BaseResponseSchema, getRequiredMessage } from '@monetize/types/common';
import { nativeEnum, z } from 'zod';
import { BillGroupDunningStatusEnumZ } from './billGroup.types';

export enum DunningStatusEnum {
  ACTIVE = 'ACTIVE',
  CANCELED = 'CANCELED',
}
export const DunningStatusEnumZ = nativeEnum(DunningStatusEnum);

export enum DunningInvoiceActionEnum {
  VOID = 'VOID',
  WRITE_OFF = 'WRITE_OFF',
}

export enum CustomerCCEnum {
  emailBillGroupCCEmails = 'emailBillGroupCCEmails',
  emailBillGroupShippingContact = 'emailBillGroupShippingContact',
  emailAccountCCEmails = 'emailAccountCCEmails',
}

export enum CustomerBCCEnum {
  emailBillGroupBCCEmails = 'emailBillGroupBCCEmails',
  emailAccountBCCEmails = 'emailAccountBCCEmails',
}

export const DunningStepCreateSchema = z.object({
  id: z.string().nullish(),
  stepName: z.string().min(1, getRequiredMessage('Name')),
  customId: z.string().nullable(),
  stepSequence: z.number(),
  daysSinceDueDate: z
    .union([z.string(), z.number()])
    .refine((val) => val !== null && val !== undefined && val !== '', {
      error: 'Day must be a valid number',
    })
    .transform((val) => Number(val)),
  retryCollection: z.boolean().default(false),
  sendEmail: z.boolean().default(false),
  emailTemplateId: z.string().nullable(),
  emailBillGroupShippingContact: z.boolean().nullable(),
  emailBillGroupCCEmails: z.boolean().nullable(),
  emailAccountCCEmails: z.boolean().nullable(),
  emailBillGroupBCCEmails: z.boolean().nullable(),
  emailAccountBCCEmails: z.boolean().nullable(),
  internalCCEmails: z.union([z.string(), z.array(z.string())]).nullable(),
  internalBCCEmails: z.union([z.string(), z.array(z.string())]).nullable(),
});

export type IDunningStepCreateSchema = z.infer<typeof DunningStepCreateSchema>;
export type DunningStepCreateFormInput = z.input<
  typeof DunningStepCreateSchema
>;

export const DunningCreateSchema = z.object({
  name: z.string().min(1, getRequiredMessage('Dunning Name')),
  dunningSteps: z
    .array(DunningStepCreateSchema)
    .superRefine((data, { addIssue }) => {
      data.forEach((step, index) => {
        const customIds = data.map(({ customId }) => customId);
        const duplicateIndex = customIds.findIndex(
          (id, i) => id && id.trim() === step.customId && i !== index,
        );

        if (duplicateIndex !== -1) {
          addIssue({
            code: 'custom',
            message: 'Custom ID should be unique',
            path: [`${index}.customId`],
          });
        }

        if (index > 0) {
          const prevDay = data[index - 1].daysSinceDueDate;
          const currDay = data[index].daysSinceDueDate;
          if (currDay <= prevDay) {
            addIssue({
              code: 'custom',
              message: 'The day should be greater than previous step day',
              path: [`${index}.daysSinceDueDate`],
            });
          }
        }

        if (
          step.internalCCEmails !== null &&
          (step.internalCCEmails as string).trim().length > 0
        ) {
          const emails = (step.internalCCEmails as string)
            .trim()
            .replace(/[,\r\n\s]/g, ',')
            .split(',')
            .filter(Boolean);

          const isAllEmailValid = emails.every(
            (email: string) => z.email().safeParse(email).success,
          );

          if (!isAllEmailValid) {
            addIssue({
              code: 'custom',
              message: 'Contains invalid email',
              path: [`${index}.internalCCEmails`],
            });
          }
        }

        const internalBCCEmailsValue = Array.isArray(step.internalBCCEmails)
          ? step.internalBCCEmails.join(',')
          : step.internalBCCEmails;

        if (
          typeof internalBCCEmailsValue === 'string' &&
          internalBCCEmailsValue.trim().length > 0
        ) {
          const emails = internalBCCEmailsValue
            .trim()
            .replace(/[,\s]/g, ',')
            .split(',')
            .filter(Boolean);

          const isAllEmailValid = emails.every(
            (email: string) => z.email().safeParse(email).success,
          );

          if (!isAllEmailValid) {
            addIssue({
              code: 'custom',
              message: 'Contains invalid email',
              path: [`${index}.internalBCCEmails`],
            });
          }
        }

        if (step.sendEmail && !step.emailTemplateId) {
          addIssue({
            code: 'custom',
            message: getRequiredMessage('Email Template Id'),
            path: [`${index}.emailTemplateId`],
          });
        }
      });
    }),
});

export type IDunningCreateSchema = z.infer<typeof DunningCreateSchema>;
export type DunningCreateFormInput = z.input<typeof DunningCreateSchema>;

export const DunningSchema = BaseResponseSchema.extend({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  invoiceEndAction: z.string(),
  dunningSteps: DunningStepCreateSchema.array(),
  totalDunningStep: z.number(),
  dunningProcessDays: z.string(),
  defaultProcess: z.boolean(),
});

export type IDunnings = z.infer<typeof DunningSchema>;

export const DunningPauseResumeReqSchema = z.object({
  billGroupId: z.string(),
  pauseData: z.object({
    dunningPauseExpirationDate: z.string().nullable(),
  }),
});
export type DunningPauseResumeReq = z.infer<typeof DunningPauseResumeReqSchema>;

export const DunningBillGroupResponseSchema = z.object({
  dunningProcessId: z.string(),
  billGroupId: z.string(),
  billGroupName: z.string(),
  accountId: z.string(),
  accountName: z.string(),
  dunningStatus: BillGroupDunningStatusEnumZ,
  currentStepId: z.string(),
  currentStepName: z.string(),
});

export type DunningBillGroupResponse = z.infer<
  typeof DunningBillGroupResponseSchema
>;

export type GetAllDunningProcessResponse = {
  dunningProcesses: IDunnings[];
  dunningProcessOptions: { label: string; value: string }[];
};
